/**
 * @file index.jsx
 * @author Archana
 * @date 2024-10-28
 * @description Show & edit the candidate details.
 */
import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import { HeaderSection } from "./HeaderSection";
import { PreferencesPanel } from "./PreferencesPanel";
import { FollowupForm } from "./FollowupForm";
import { Remarks } from "./Remarks";
import { useFetchData } from "../../components/hooks/useFetchData";
import { CandidatePopup } from "../LeadsPage/LeadDetails/CandidatePopup";

const CandidateDetails = () => {
  const { id } = useParams();
  const [minLoading, setMinLoading] = React.useState(true);
  const [lead, setLead] = React.useState([]);
  const [countryList, setCountryList] = React.useState([]);
  const [courseList, setCourseList] = React.useState([]);
  const [followupTypes, setFollowupTypes] = useState([]);
  const [isConvertPopupOpen, setIsConvertPopupOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = React.useState({});
  const [selectedCourse, setSelectedCourse] = React.useState({});
  const [courseCountryList, setCourseCountryList] = React.useState([]);

  const today = dayjs();

  const { fetchData, loading } = useFetchData();
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    control,
    reset,
  } = useForm({
    defaultValues: {
      candidateuuid: lead?.candidateuuid || "",
      followupid: lead?.followuplist?.[0]?.id || "",
      followupnotes: lead?.followuplist?.[0]?.followupnotes || "",
      followuptype: lead?.followuplist?.[0]?.followuptype || "",
      followupdate: lead?.followuplist?.[0]?.followupdate
        ? dayjs(lead.followuplist[0].followupdate)
        : null,
      followupoutcome: "",
      nextfollowupdate: null,
      nextfollowuptype: "",
      nextfollowupnotes: "",
    },
  });

  const getAllCountries = async () => {
    const result = await fetchData("getcountries?showActive=1");
    if (result?.code === 200) {
      setCountryList(result.data);
    }
  };

  const getAllCourses = async () => {
    const result = await fetchData("getcourses?showActive=1");
    if (result?.code === 200) {
      setCourseList(result.data);
    }
  };

  const getCandidateDetails = async () => {
    const body = {
      candidateuuid: id,
    };
    const result = await fetchData("getcandidatedetails", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setLead(result.data);
    }
  };

  const getFollowupTypes = async () => {
    const result = await fetchData("getfollowuptypes");
    if (result?.code === 200) {
      setFollowupTypes(result.data);
    }
  };

  const getCoursesByCountry = async () => {
    if (!selectedCountry) return;
    const body = { countryid: selectedCountry?.id };
    const result = await fetchData("getcoursesbycountry", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setCourseCountryList(result.data);
    }
  };

  React.useEffect(() => {
    const fetchDataAsync = async () => {
      await getAllCountries();
      await getAllCourses();
      await getCandidateDetails();
      await getFollowupTypes();
      await getCoursesByCountry();
    };

    fetchDataAsync();
    const minLoadingTimer = setTimeout(() => {
      setMinLoading(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const fetchDataAsync = async () => {
      await getCoursesByCountry();
    };

    fetchDataAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  React.useEffect(() => {
    if (lead) {
      setValue("candidateuuid", lead.candidateuuid || "");
      setValue("followupid", lead.followuplist?.[0]?.id || "");
      setValue("followupnotes", lead.followuplist?.[0]?.followupnotes || "");
      setValue("followuptype", lead.followuplist?.[0]?.followuptype || "");
      setValue(
        "followupdate",
        lead.followuplist?.[0]?.followupdate
          ? dayjs(lead.followuplist[0].followupdate)
          : null
      );
    }
  }, [lead, setValue]);

  const handleEditPreferences = () => {
    getCoursesByCountry();
    setIsConvertPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsConvertPopupOpen(false);
  };

  const handleEditCandidate = async (data) => {
    const enquirydate = dayjs(data.enquirydate).format("YYYY-MM-DD");
    const body = {
      ...lead,
      name: data.name,
      email: data.email,
      personalphone: data.personalphone,
      enquirydate: enquirydate,
      coursecountry: [
        { countryid: selectedCountry?.id, courses: [selectedCourse?.courseid] },
      ],
    };
    const result = await fetchData("managecandidate", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      setSelectedCountry({});
      setSelectedCourse({});
      setIsConvertPopupOpen(false);
      getCandidateDetails();
    } else {
      enqueueSnackbar(result?.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  // calling candidate
  const handleCall = async () => {
    const body = {
      candidateuuid: lead.candidateuuid,
    };
    const result = await fetchData("makecall", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      getCandidateDetails();
    } else {
      enqueueSnackbar(result.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  const onSubmit = async (data) => {
    const body = {
      candidateuuid: lead.candidateuuid,
      followupid: data.followupid,
      followuptype: data.followuptype,
      followupoutcome: data.followupoutcome,
      nextfollowupdate: dayjs(data.nextfollowupdate).format("YYYY-MM-DD"),
      nextfollowuptype: data.nextfollowuptype,
      nextfollowupnotes: data.nextfollowupnotes,
    };
    const result = await fetchData("updatefollowup", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      reset({
        candidateuuid: lead.candidateuuid,
        followupid: "",
        followupnotes: "",
        followuptype: "",
        followupdate: null,
        followupoutcome: "",
        nextfollowupdate: null,
        nextfollowuptype: "",
        nextfollowupnotes: "",
      });
      getCandidateDetails();
    } else {
      enqueueSnackbar(result.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  const isLoading =
    minLoading ||
    loading["getcountries?showActive=1"] ||
    loading["getcourses?showActive=1"] ||
    loading["getcandidatedetails"];

  return (
    <>
      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        width="100%"
        height="100%"
        overflow={{ xs: "auto", md: "hidden" }}
      >
        <HeaderSection
          lead={lead}
          isLoading={isLoading}
          handleCall={handleCall}
        />
        <Stack
          flex={1}
          display="flex"
          columnGap={1}
          flexDirection={{ xs: "column", md: "row" }}
          overflow={{ xs: "unset", md: "hidden" }}
          pt={1}
          rowGap={{ xs: 2, md: 0 }}
        >
          <Stack
            width={{ xs: "100%", md: "40%" }}
            height="100%"
            overflow={{ xs: "unset", md: "auto" }}
          >
            <Stack>
              {isLoading ? (
                <Skeleton
                  variant="rounded"
                  sx={{ mt: 1, mb: 1 }}
                  width={140}
                  height={24}
                />
              ) : (
                <Typography
                  sx={{ mt: 1 }}
                  fontWeight={600}
                  fontSize={20}
                  color="#222222"
                >
                  Follow-up
                </Typography>
              )}
            </Stack>
            <Divider />
            <Stack pt={1} flex={1} rowGap={1}>
              <FollowupForm
                isLoading={isLoading}
                followupTypes={followupTypes}
                register={register}
                errors={errors}
                control={control}
                watch={watch}
                setValue={setValue}
                onSubmit={handleSubmit(onSubmit)}
                followuplist={lead?.followuplist || []}
                today={today}
              />
            </Stack>
          </Stack>
          <Stack
            flex={1}
            // overflow={{ xs: "auto", md: "hidden" }}
            rowGap={1}
            display="flex"
            flexDirection="column"
          >
            <PreferencesPanel
              isLoading={isLoading}
              courseCountryData={lead?.coursecountry || []}
              countryList={countryList}
              courseList={courseList}
              handleEditPreferences={handleEditPreferences}
            />
            <Remarks
              isLoading={isLoading}
              followuplist={lead?.followuplist || []}
            />
          </Stack>
        </Stack>
      </Box>
      <CandidatePopup
        open={isConvertPopupOpen}
        onClose={handleClosePopup}
        onSubmit={handleEditCandidate}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        hideRegistrationFee={true}
        countryList={countryList || []}
        courseCountryList={courseCountryList || []}
        lead={lead}
        btn2Text="Submit"
        title="Edit Candidate"
        description="You can edit your candidate details here."
      />
    </>
  );
};

export default CandidateDetails;
